import React, { useState, useEffect, useCallback } from "react";
import "./App.css";
import Calendar from "./components/Calendar";
import Finance from "./components/Finance";
import Reminders from "./components/Reminders";
import ExecutionLog from "./components/ExecutionLog";
import SuperuserDbSnapshot from "./components/SuperuserDbSnapshot";
import SuperuserTelegramSupport from "./components/SuperuserTelegramSupport";
import SuperuserAgentChatLog from "./components/SuperuserAgentChatLog";
import Login from "./components/Login";
import { useAuth } from "./context/AuthContext";
import { useMediaQuery } from "./hooks/useMediaQuery";
import "./design.css";

const MAIN = [
  ["calendar", "Календарь"],
  ["finance", "Финансы"],
  ["reminders", "Напоминания"],
];
const EXTRA = [
  ["execution-log", "История агента"],
  ["support-telegram", "Поддержка Telegram"],
  ["db-tables", "База данных"],
  ["agent-chat-log", "Диалоги агента"],
];
const ICONS = {
  calendar: "M8 2v4m8-4v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14H3V6a2 2 0 0 1 2-2Z",
  finance: "M3 5h16v4H3zM3 9h18v12H3zM16 14h5",
  reminders: "M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9M9 21h6",
  more: "M4 6h16M4 12h16M4 18h16",
  logout: "M10 5H5v14h5M14 8l4 4-4 4M18 12H9",
};
function Icon({ name }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={ICONS[name] || ICONS.more} />
    </svg>
  );
}
function readTab(admin) {
  const value = window.location.hash.slice(1);
  return [...MAIN, ...(admin ? EXTRA : EXTRA.slice(0, 1))].some(
    ([id]) => id === value,
  )
    ? value
    : "calendar";
}

export default function App() {
  const { user, actor, loading, logout, impersonationUsers, setActAsUser } =
    useAuth();
  const mobile = useMediaQuery("(max-width: 768px)");
  const [active, setActive] = useState(() => readTab(false));
  const [more, setMore] = useState(false);
  const [profileError, setProfileError] = useState("");
  const select = useCallback((id) => {
    window.location.hash = id;
    setActive(id);
    setMore(false);
  }, []);
  useEffect(() => {
    const change = () => setActive(readTab(actor?.is_superuser));
    change();
    window.addEventListener("hashchange", change);
    return () => window.removeEventListener("hashchange", change);
  }, [actor?.is_superuser]);
  useEffect(() => {
    if (!more) return;
    const close = (e) => {
      if (e.key === "Escape") setMore(false);
    };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [more]);
  if (loading) return <div className="App-loading">Загрузка…</div>;
  if (!user) return <Login />;
  const label = [...MAIN, ...EXTRA].find(([id]) => id === active)?.[1];
  const nav = ([id, title]) => (
    <button
      key={id}
      className={`Shell-link ${active === id ? "is-active" : ""}`}
      aria-current={active === id ? "page" : undefined}
      onClick={() => select(id)}
    >
      <Icon name={id} />
      <span>{title}</span>
    </button>
  );
  const profile = (
    <div className="Shell-profile">
      <strong>{user.username}</strong>
      {actor?.is_superuser && (
        <label>
          От имени
          <select
            aria-label="Работать от имени"
            value={user.id === actor.id ? "" : String(user.id)}
            onChange={async (e) => {
              try {
                await setActAsUser(
                  e.target.value ? Number(e.target.value) : null,
                );
                setProfileError("");
              } catch (err) {
                setProfileError(err.message);
              }
            }}
          >
            <option value="">Я ({actor.username})</option>
            {impersonationUsers
              .filter((u) => u.id !== actor.id)
              .map((u) => (
                <option key={u.id} value={u.id}>
                  {u.username}
                </option>
              ))}
          </select>
        </label>
      )}
      {profileError && <p role="alert">{profileError}</p>}
    </div>
  );
  return (
    <div className="App Shell">
      {!mobile && (
        <aside className="Shell-sidebar">
          <div className="Shell-brand">
            <span className="Shell-brand-mark">K</span>
            <div>
              Khagronflow<small>Личный помощник</small>
            </div>
          </div>
          <nav aria-label="Основные разделы">{MAIN.map(nav)}</nav>
          <div className="Shell-admin">
            <div className="Shell-caption">Инструменты</div>
            {(actor?.is_superuser ? EXTRA : EXTRA.slice(0, 1)).map(nav)}
          </div>
          {profile}
        </aside>
      )}
      <main key={user.id} className="App-content Shell-main">
        {mobile && (
          <header className="Shell-mobile-header">
            <span>Khagronflow</span>
            <div className="Shell-mobile-actions">
              <button
                className="Shell-secondary"
                aria-expanded={more}
                aria-controls="shell-more"
                onClick={() => setMore(!more)}
              >
                Профиль и меню
              </button>
              <button className="Shell-logout" onClick={logout} aria-label="Выйти">
                <Icon name="logout" />
              </button>
            </div>
          </header>
        )}
        <div className="Shell-breadcrumb">
          <div>Личное пространство <span>/</span> {label}</div>
          <button className="Shell-logout" onClick={logout}>
            <Icon name="logout" />
            <span>Выйти</span>
          </button>
        </div>
        <div className="App-tab-pane" hidden={active !== "calendar"}>
          <Calendar />
        </div>
        <div className="App-tab-pane" hidden={active !== "finance"}>
          <Finance />
        </div>
        <div className="App-tab-pane" hidden={active !== "reminders"}>
          <Reminders />
        </div>
        <div className="App-tab-pane" hidden={active !== "execution-log"}>
          <ExecutionLog />
        </div>
        {actor?.is_superuser && (
          <>
            <div
              className="App-tab-pane"
              hidden={active !== "support-telegram"}
            >
              <SuperuserTelegramSupport
                active={active === "support-telegram"}
              />
            </div>
            <div className="App-tab-pane" hidden={active !== "db-tables"}>
              <SuperuserDbSnapshot />
            </div>
            <div className="App-tab-pane" hidden={active !== "agent-chat-log"}>
              <SuperuserAgentChatLog active={active === "agent-chat-log"} />
            </div>
          </>
        )}
      </main>
      {mobile && (
        <nav className="Shell-bottom" aria-label="Основные разделы">
          {MAIN.map(nav)}
        </nav>
      )}
      {mobile && more && (
        <>
          <button
            className="Shell-backdrop"
            aria-label="Закрыть меню"
            onClick={() => setMore(false)}
          />
          <section
            className="Shell-more"
            id="shell-more"
            aria-label="Профиль и дополнительные разделы"
          >
            <div className="Shell-more-head">
              <strong>Профиль и меню</strong>
              <button
                onClick={() => setMore(false)}
                className="Shell-secondary"
              >
                Закрыть
              </button>
            </div>
            {profile}
            <nav>
              {(actor?.is_superuser ? EXTRA : EXTRA.slice(0, 1)).map(nav)}
            </nav>
          </section>
        </>
      )}
    </div>
  );
}
